/** Fusionne les réponses GET /capitalflow/strategies vers allS / allComp / stratInv / investors. */

import { STATIC_INVESTORS } from "./strategiesStaticEnrich";

function toNum(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeDbSignal(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).toUpperCase();
  if (s === "BUY" || s.includes("ACCUMULATE")) return "BUY";
  if (s === "HOLD") return "HOLD";
  return "WATCH";
}

function toMosPct(v) {
  let m = toNum(v);
  if (m == null) return null;
  if (Math.abs(m) < 2.5) m = m * 100;
  if (Math.abs(m) > 200) return null;
  return m;
}

/** Mappe les colonnes SQL/API vers les champs attendus par IndexStrategiesPage. */
export function parseComp(c) {
  const sigRaw = c.dd_signal ?? c.signal ?? c._sig ?? null;
  return {
    ...c,
    _is: toNum(c.iscore ?? c._is),
    _fn: toNum(c.fund_score ?? c._fn),
    _ti: toNum(c.trend_score ?? c._ti),
    _mo: toMosPct(c.mos_pct ?? c._mo),
    _sig: normalizeDbSignal(sigRaw),
  };
}

function initialsFromName(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

/** Investisseurs dérivés de stratInv + liste API (sans dépendre de INVESTORS statique). */
export function buildInvestorsMap(api, staticEnrich = {}) {
  const map = {};
  (api.investors || []).forEach((inv) => {
    if (!inv?.name) return;
    map[inv.name] = {
      ini: inv.ini || initialsFromName(inv.name),
      style: inv.style || "Value",
      bio: inv.bio || "",
      philosophy: inv.philosophy || "",
    };
  });

  const stratInv = api.stratInv || {};
  Object.values(stratInv).forEach((name) => {
    if (!name || map[name]) return;
    const enrich = staticEnrich[name];
    map[name] = enrich
      ? { ...enrich }
      : {
          ini: initialsFromName(name),
          style: "Value",
          bio: "",
          philosophy: "",
        };
  });

  (api.strategies || []).forEach((s) => {
    const name = s.investor;
    if (!name || map[name]) return;
    const enrich = staticEnrich[name];
    map[name] = enrich
      ? { ...enrich }
      : {
          ini: initialsFromName(name),
          style: "Value",
          bio: "",
          philosophy: "",
        };
  });

  return map;
}

export function buildStratInvFromApi(api) {
  const out = { ...(api.stratInv || {}) };
  (api.links || []).forEach((l) => {
    if (l.strategy && l.investor) out[l.strategy] = l.investor;
  });
  (api.strategies || []).forEach((s) => {
    if (s.name && s.investor) out[s.name] = s.investor;
  });
  return out;
}

export function mergeStrategiesPagePayload(prev, api) {
  if (!api || api.error) return prev;
  const out = {
    allS: prev?.allS ? [...prev.allS] : [],
    allComp: prev?.allComp ? [...prev.allComp] : [],
    stratInv: { ...(prev?.stratInv || {}) },
    investorsMap: { ...(prev?.investorsMap || {}) },
  };

  const byKey = new Map();
  out.allS.forEach((s) => {
    byKey.set(`${s.name}|${s.analYear}`, s);
  });
  (api.strategies || []).forEach((s) => {
    byKey.set(`${s.name}|${s.analYear}`, s);
  });
  out.allS = Array.from(byKey.values());

  out.stratInv = buildStratInvFromApi({
    stratInv: { ...out.stratInv, ...(api.stratInv || {}) },
    links: api.links,
    strategies: api.strategies,
  });

  const compKey = (c) => `${c.strategy}|${c.symbol}`;
  const compMap = new Map();
  out.allComp.forEach((c) => compMap.set(compKey(c), c));
  (api.constituents || []).forEach((c) => compMap.set(compKey(c), parseComp(c)));
  out.allComp = Array.from(compMap.values());

  out.investorsMap = buildInvestorsMap(
    { ...api, stratInv: out.stratInv },
    { ...STATIC_INVESTORS, ...(prev?.investorsMap || {}) }
  );

  return out;
}

export function buildInvestorOverrides(api) {
  const overrides = {};
  (api.investors || []).forEach((inv) => {
    if (!inv.name) return;
    const cur = overrides[inv.name] || {};
    overrides[inv.name] = {
      ...cur,
      ...(inv.style ? { style: inv.style } : {}),
      ...(inv.bio ? { bio: inv.bio } : {}),
      ...(inv.philosophy ? { philosophy: inv.philosophy } : {}),
      ...(inv.ini ? { ini: inv.ini } : {}),
    };
  });
  return overrides;
}
